/**
 * Belge talepleri — müşteri tarafı.
 *
 *   · Home'da "Pending requests" kutusu: açık talepler ve eksik belgeler.
 *   · Her eksik belgeye uygulama içinden yükleme: dosya müşterinin kendi
 *     klasörüne yazılır ({uid}/requests/{talep}/…), client_documents satırı
 *     açılır, attach_document_to_request kaleme bağlar. Hepsi gelince talep
 *     kapanır ve ofis bildirim alır (veritabanında).
 *   · AI sayfası açılınca orb bekleyen talebi söyler.
 *
 * Kurallar veritabanında (20260917_05_document_requests.sql); burada güvenlik yok.
 * Ofisin talep oluşturma ekranı Faz 6'da (ofis çalışma alanı).
 *
 * KALDIRMAK İÇİN: klasörü sil, shell/index.ts satırını çıkar, index.html'deki
 * #dr-home-box kabını kaldır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { TEXTS, KIND_LABEL } from './texts.ts';

interface Item { id: string; kind: string; label: string; status: string; position: number }
interface Req {
  id: string; title: string; message: string; period: string; due_date: string | null;
  created_at: string; document_request_items: Item[];
}

let pending: Req[] = [];
let hinted = false;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
/* Metinler İLK KULLANIMDA kaydedilir: bu özellik v6'ya bağlı olduğu için kabuk
   boot() çağırmaz (bootAll yalnızca görünümsüz özellikleri başlatır). */
let textsReady = false;
function t(k: string): string {
  if (!textsReady) { legacy.addTranslations(TEXTS); textsReady = true; }
  return legacy.t(k);
}
function isCustomer(): boolean {
  const g = globalThis as unknown as { atIsCustomer?: () => boolean };
  return typeof g.atIsCustomer === 'function' ? g.atIsCustomer() : false;
}

async function load(): Promise<Req[]> {
  const sb = legacy.sb;
  if (!sb) return [];
  const res = await sb.from('document_requests')
    .select('id,title,message,period,due_date,created_at,document_request_items(id,kind,label,status,position)')
    .eq('status', 'open')
    .order('created_at', { ascending: false })
    .limit(20);
  if (!res || res.error) return [];
  return (res.data as Req[]) || [];
}

function missing(r: Req): Item[] {
  return (r.document_request_items || []).filter((i) => i.status === 'missing')
    .sort((a, b) => a.position - b.position);
}

async function render(): Promise<void> {
  const host = el('dr-home-box');
  if (!isCustomer()) { if (host) host.innerHTML = ''; pending = []; return; }

  /* Kutu yoksa da yüklenir: AI sayfasının "From your accountant" kartı aynı listeyi okur. */
  pending = (await load()).filter((r) => missing(r).length > 0);
  if (!host) return;
  if (!pending.length) { host.innerHTML = ''; return; }

  host.innerHTML = '<div class="card" style="margin-bottom:12px">'
    + '<div class="set">' + esc(t('dr_title')) + ' <span translate="no" style="color:var(--red)">' + pending.length + '</span></div>'
    + pending.map((r) => {
      const due = r.due_date ? ' · ' + esc(t('dr_due')) + ' <span translate="no">' + esc(r.due_date) + '</span>' : '';
      const items = missing(r).map((i) =>
        '<div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-top:1px solid var(--div)">'
        + '<span style="flex:1;font-size:12px">' + esc(KIND_LABEL[i.kind] ?? i.kind) + (i.label ? ' — ' + esc(i.label) : '') + '</span>'
        + '<button class="btn br bsm" onclick="drUpload(\'' + esc(r.id) + '\',\'' + esc(i.id) + '\',this)">' + esc(t('dr_upload')) + '</button>'
        + '</div>').join('');
      return '<div style="margin-top:12px">'
        + '<div style="font-size:13px;font-weight:600">' + esc(r.title) + '</div>'
        + '<div style="font-size:11px;color:var(--t2);margin-bottom:6px">'
        + (r.period ? '<span translate="no">' + esc(r.period) + '</span>' : esc(t('dr_from'))) + due + '</div>'
        + (r.message ? '<div style="font-size:11px;color:var(--t2);white-space:pre-line;margin-bottom:6px">' + esc(r.message) + '</div>' : '')
        + items + '</div>';
    }).join('')
    + '</div>';
}

function safeName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  return (base.normalize('NFKD').replace(/[^\w.\-]+/g, '_').replace(/^\.+/, '') || 'document').slice(-120);
}

const ALLOWED = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'xlsx', 'xls', 'csv', 'xml', 'txt', 'docx'];

function upload(requestId: string, itemId: string, btn: HTMLButtonElement | null): void {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = '.pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.xml,.txt,.docx,image/*';
  inp.onchange = async () => {
    const f = inp.files && inp.files[0];
    if (!f) return;
    const name = safeName(f.name);
    const ext = (name.split('.').pop() ?? '').toLowerCase();
    if (!ALLOWED.includes(ext)) { legacy.toast(t('dr_type')); return; }
    if (f.size > 20 * 1024 * 1024) { legacy.toast(t('dr_size')); return; }

    const g = globalThis as unknown as { _sbUser?: { id: string } | null; _sb?: any };
    const sb = g._sb;
    const uid = g._sbUser?.id;
    const pid = (legacy.user as { sbProfileId?: string } | null)?.sbProfileId;
    if (!sb || !uid || !pid) { legacy.toast(t('dr_signin')); return; }

    if (btn) { btn.disabled = true; btn.textContent = t('dr_uploading'); }
    try {
      const path = `${uid}/requests/${requestId}/${Date.now()}_${name}`;
      const up = await sb.storage.from('client-files').upload(path, f, {
        contentType: f.type || 'application/octet-stream', upsert: false,
      });
      if (up.error) throw up.error;

      const ins = await sb.from('client_documents').insert({
        profile_id: pid, contact_id: null, name, file_type: ext, storage_path: path, size_bytes: f.size,
      }).select('id').single();
      if (ins.error || !ins.data) {
        /* Satır açılamadıysa yetim dosya bırakma. */
        await sb.storage.from('client-files').remove([path]);
        throw ins.error ?? new Error('insert');
      }

      const r = await sb.rpc('attach_document_to_request', { p_item_id: itemId, p_document_id: ins.data.id });
      const data = (r && r.data) || {};
      if (r.error || data.ok !== true) throw new Error(data.error || 'attach');
      legacy.toast(data.fulfilled ? t('dr_all_done') : t('dr_done'));
    } catch (e) {
      const msg = e && typeof (e as Error).message === 'string' && !/^(insert|attach)$/.test((e as Error).message)
        ? (e as Error).message : t('dr_fail');
      legacy.toast(msg);
    } finally {
      void render();
    }
  };
  inp.click();
}

/** AI sayfası açıldığında bir kez: orb bekleyen talebi söyler. */
function orbHint(): void {
  if (hinted || !pending.length) return;
  const g = globalThis as unknown as { aiShowBubble?: (html: string, autoHide?: boolean) => void };
  if (typeof g.aiShowBubble !== 'function') return;
  hinted = true;
  const n = pending.reduce((s, r) => s + missing(r).length, 0);
  const text = n === 1 ? t('dr_orb_one') : t('dr_orb_many').replace('{n}', String(n));
  setTimeout(() => g.aiShowBubble?.(esc(text), true), 1200);
}

export default defineFeature({
  id: 'doc-requests',
  label: 'Belge talepleri (müşteri)',
  /* AI sayfası açıldığında orb hatırlatması için bağlanır; sayfanın içeriğine dokunmaz. */
  view: 'v6',

  /* Aynı sayfanın ofis düzeni firm-ai'de; kayıt tek view'a bir modül izin verdiği için buradan çağrılır. */
  mount: () => { orbHint(); (globalThis as any).fwaiApply?.(); (globalThis as any).cuaiApply?.(); },

  legacyGlobals: {
    drRender: () => render(),
    drUpload: (requestId: unknown, itemId: unknown, btn: unknown) =>
      upload(String(requestId), String(itemId), btn instanceof HTMLButtonElement ? btn : null),
    drPendingCount: () => pending.length,
    /** Eksik kalemler düz liste: AI sayfası sağ paneli. */
    drPendingItems: async () => {
      await render();
      return pending.flatMap((r) => missing(r).map((i) => ({ requestId: r.id, itemId: i.id, title: r.title,
        label: (KIND_LABEL[i.kind] ?? i.kind) + (i.label ? ' — ' + i.label : ''), due: r.due_date })));
    },
  },

  owns: { tables: ['document_requests', 'document_request_items'] },
});
